import { routeRolloutRegistry } from '@usealloy/daffy-to-frontend-dashboard-rollout';
import { generatePath, useLocation } from 'react-router-dom';
import { useContext } from 'react';
import { toCamel } from '~/helpers/api';
import { UserContext } from '../context';
import { findMatchedGroupAndPath } from './helpers';

interface FDMigrationGateProps {
  children: React.ReactNode;
}

/**
 * This component acts as a top-level gate that observes the url state for any route, and
 * conditionally redirects the user to the frontend dashboard if the route is enabled for them.
 *
 * The logic here is intentionally implemented in the body of the component instead of in an effect,
 * so that React evaluates the gate before having to mount the children. Doing so avoids a flash
 * of content when the gate redirects the user away.
 *
 * A frontend-dashboard counterpart exists: BetaGate
 */
export default function FDMigrationGate({ children }: FDMigrationGateProps) {
  const { hash, pathname, search } = useLocation();
  const { betaCookies, featureFlags } = useContext(UserContext);

  // escape hatch that disables redirects – this flag can only ever be set in local dev.
  if (featureFlags?.betaDisableRedirectsLocally) {
    return children;
  }

  const matched = findMatchedGroupAndPath({
    routeRegistry: routeRolloutRegistry,
    daffyPathname: pathname,
  });

  if (matched) {
    const { matchedRouteGroup, matchedPath, params } = matched;

    const routeIsEnabledForUser = Boolean(featureFlags?.[toCamel(matchedRouteGroup.featureFlag)]);
    const userHasOptedIntoRoute = Boolean(betaCookies?.[matchedRouteGroup.cookie]);

    if (routeIsEnabledForUser && userHasOptedIntoRoute) {
      // [if needed, add datadog reporting here]
      /**
       * build the frontend-dashboard pathname with any dynamic path params daffy might contain
       * eg: (daffy)/path/123 -> (frontend-dashboard)/path/123. without this, the verbatim
       * frontend-dashboard pathname would be used (/path/:id)
       */
      const frontendDashboardPathname = generatePath(matchedPath.frontendDashboard, params);
      /**
       * If a future path needs to modify or transform `search` or `hash`, implement a `pathTransformer`
       * function in the daffy-to-frontend-dashboard jslib for the route in question, and invoke it here
       * instead of the naive transformer.
       * For example: ?name=foo in daffy to become ?firstName=foo in frontend-dashboard
       */
      const fdPath = `${window.location.origin}${frontendDashboardPathname}${search}${hash}`;
      window.location.replace(fdPath);
      return null;
    }
  }

  return children;
}
